/**
 * Right-sliding iframe panel used by the Pearl product icons (including Voice
 * when the inline-panel toggle is enabled).
 *
 * Uses the `.pw-pnl*` CSS classes defined in `shadow-dom.ts`. Close via
 * backdrop click, explicit close pill (mini-tab style anchored to the left
 * edge of the panel — chevron points right, "click to push the panel
 * away"), or Esc.
 *
 * When opened in **voice mode** (`bottomRight = true`), the panel listens
 * for `SCRIBE_EMBEDDED_RESIZE` `postMessage` events from the Voice iframe
 * and sizes `.pw-pnl-body` to match, so the floating panel wraps the
 * embedded view tightly instead of using a hardcoded size.
 */

import { MSG, isObject } from '../contracts/index';
import {
  PRELOAD_ATTR,
  PRELOAD_LOADED_ATTR,
  PENDING_SRC_ATTR,
  LOAD_ON_OPEN_ATTR,
} from './preload-attrs';

/**
 * Right-pointing chevron used by the panel's left-edge close pill. Mirror of
 * the bottom-right mini-tab's left chevron (CHEVRON_LEFT_SVG in
 * pearl-shell.ts). Same stroke width / stroke colour / 24×24 viewBox so the
 * pill reads as the same family of affordance.
 */
const CHEVRON_RIGHT_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M9.5 7l5 5-5 5" stroke="#050505" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

const CHEVRON_LEFT_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M14.5 7l-5 5 5 5" stroke="#050505" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

export const INLINE_PANEL_SANDBOX =
  'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads';

export const VOICE_PANEL_SANDBOX = INLINE_PANEL_SANDBOX;

/** Matches the CSS `.pw-pnl-body` slide-out transition duration (420ms). */
export const CLOSE_ANIMATION_MS = 420;

export interface InlinePanelCallbacks {
  onOpen?: (url: string) => void;
  onClose?: () => void;
  /** Yield/restore the Pearl launcher while a preserved panel is minimized. */
  onPreservedMinimizedChange?: (minimized: boolean) => void;
  /** Resolve a hidden preload iframe staged in `.pw-pnl-body`, if any. */
  getPreloadedIframe?: (url: string) => HTMLIFrameElement | null;
  /** Override for tests so animation-gated cleanup is observable synchronously. */
  setTimeoutFn?: (fn: () => void, ms: number) => unknown;
}

export interface InlinePanelOpenOptions {
  /** Keep the iframe mounted when the user dismisses the panel. */
  preserveOnDismiss?: boolean;
}

export interface InlinePanel {
  open: (
    url: string,
    bottomRight?: boolean,
    options?: InlinePanelOpenOptions,
  ) => void;
  close: () => void;
  /** Reveal an iframe retained by a preserve-on-dismiss open. */
  show: () => boolean;
  isOpen: () => boolean;
  destroy: () => void;
  /** The `.pw-pnl-body` element — preload pool appends hidden iframes here. */
  getBody: () => HTMLElement;
  /** The `.pw-pnl` root element — used for zoom compensation. */
  getRoot: () => HTMLElement;
}

type ShadowLike = ShadowRoot | HTMLElement;

function isPreloadedIframe(el: HTMLIFrameElement | null): boolean {
  return !!el && el.hasAttribute(PRELOAD_ATTR);
}

export function createInlinePanel(
  doc: Document,
  shadow: ShadowLike,
  callbacks: InlinePanelCallbacks = {},
): InlinePanel {
  const setTimeoutFn =
    callbacks.setTimeoutFn ||
    ((fn: () => void, ms: number) =>
      (
        doc.defaultView as unknown as { setTimeout?: typeof setTimeout }
      )?.setTimeout?.(fn, ms) ?? 0);
  const getPreloaded = callbacks.getPreloadedIframe;
  const pnl = doc.createElement('div');
  pnl.className = 'pw-pnl';

  const body = doc.createElement('div');
  body.className = 'pw-pnl-body';

  const closeBtn = doc.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'pw-pnl-close';
  closeBtn.setAttribute('aria-label', 'Close panel');
  closeBtn.innerHTML = CHEVRON_RIGHT_SVG;

  const resumeBtn = doc.createElement('button');
  resumeBtn.type = 'button';
  resumeBtn.className = 'pw-pnl-resume';
  resumeBtn.setAttribute('aria-label', 'Resume perio chart');
  resumeBtn.setAttribute('aria-hidden', 'true');
  resumeBtn.disabled = true;
  resumeBtn.tabIndex = -1;
  resumeBtn.innerHTML =
    CHEVRON_LEFT_SVG +
    '<span class="pw-pnl-resume-dot" aria-hidden="true"></span>' +
    '<span class="pw-pnl-resume-label">Resume perio</span>';

  const loading = doc.createElement('div');
  loading.className = 'pw-pnl-loading';
  loading.innerHTML = '<div class="pw-pnl-spinner"></div> Loading\u2026';

  body.appendChild(closeBtn);
  body.appendChild(loading);
  pnl.appendChild(body);
  pnl.appendChild(resumeBtn);
  shadow.appendChild(pnl);

  let currentIframe: HTMLIFrameElement | null = null;
  let openState = false;
  let isVoiceMode = false;
  let preserveOnDismiss = false;
  let preservedMinimized = false;
  let lifecycleGeneration = 0;

  const PANEL_INSET = 16;
  const win = doc.defaultView;

  function handleResizeMessage(event: MessageEvent): void {
    if (!isVoiceMode || !openState) return;
    if (!isObject(event.data)) return;
    if (currentIframe && event.source !== currentIframe.contentWindow) return;
    if (event.data.type !== MSG.EMBEDDED_RESIZE) return;

    const w = event.data.width as number;
    const h = event.data.height as number;
    if (typeof w === 'number' && w > 0) {
      const maxW = win
        ? (win.innerWidth || doc.documentElement.clientWidth) - PANEL_INSET * 2
        : w;
      body.style.width = `${Math.min(Math.round(w), maxW)}px`;
    }
    if (typeof h === 'number' && h > 0) {
      const maxH = win
        ? (win.innerHeight || doc.documentElement.clientHeight) -
          PANEL_INSET * 2
        : h;
      body.style.height = `${Math.min(Math.round(h), maxH)}px`;
    }

    const borderRadius = event.data.borderRadius as number | undefined;
    if (typeof borderRadius === 'number') {
      body.style.borderRadius = `${borderRadius}px`;
      if (currentIframe) currentIframe.style.borderRadius = `${borderRadius}px`;
    }
  }

  if (win) win.addEventListener('message', handleResizeMessage);

  /**
   * Hides a pooled iframe and reloads it so the next open starts fresh. The
   * reload clears the loaded marker, otherwise the next open would hide the
   * spinner over a frame that is still blank. A load-on-open iframe is
   * unloaded instead and waits for the next `claimForOpen`.
   */
  function parkPreloaded(iframe: HTMLIFrameElement): void {
    iframe.style.display = 'none';
    const src = iframe.src;
    if (!src) return;
    iframe.removeAttribute(PRELOAD_LOADED_ATTR);
    if (iframe.hasAttribute(LOAD_ON_OPEN_ATTR)) {
      iframe.setAttribute(PENDING_SRC_ATTR, src);
      iframe.removeAttribute('src');
      return;
    }
    iframe.src = src;
  }

  /** Keeps the loading state up until a claimed pooled iframe has content. */
  function showLoadingUntilLoaded(iframe: HTMLIFrameElement): void {
    if (iframe.hasAttribute(PRELOAD_LOADED_ATTR)) {
      loading.style.display = 'none';
      return;
    }
    loading.style.display = 'flex';
    iframe.addEventListener(
      'load',
      () => {
        if (currentIframe === iframe) loading.style.display = 'none';
      },
      { once: true },
    );
  }

  function postPanelOpen(iframe: HTMLIFrameElement, targetUrl: string): void {
    try {
      let targetOrigin = '*';
      try {
        targetOrigin = new URL(targetUrl, doc.baseURI).origin;
      } catch {
        /* fallback to '*' */
      }
      iframe.contentWindow?.postMessage(
        { type: 'PEARL_INLINE_PANEL_OPEN', url: targetUrl },
        targetOrigin,
      );
    } catch {
      /* ignore cross-origin or detached frame errors */
    }
  }

  function setPreservedMinimized(next: boolean): void {
    if (preservedMinimized === next) return;
    preservedMinimized = next;
    resumeBtn.disabled = !next;
    resumeBtn.tabIndex = next ? 0 : -1;
    resumeBtn.setAttribute('aria-hidden', String(!next));
    callbacks.onPreservedMinimizedChange?.(next);
  }

  function reveal(): void {
    const revealGeneration = ++lifecycleGeneration;
    setPreservedMinimized(false);
    pnl.classList.remove('pw-pnl-minimized');
    pnl.style.display = 'flex';
    body.inert = false;
    body.removeAttribute('aria-hidden');
    openState = true;
    const win = doc.defaultView;
    if (win && typeof win.requestAnimationFrame === 'function') {
      win.requestAnimationFrame(() => {
        win.requestAnimationFrame(() => {
          if (openState && revealGeneration === lifecycleGeneration) {
            pnl.classList.add('open');
          }
        });
      });
    } else {
      pnl.classList.add('open');
    }
  }

  function hide(): void {
    if (!openState) return;
    lifecycleGeneration++;
    openState = false;
    pnl.classList.remove('open');
    pnl.classList.add('pw-pnl-minimized');
    pnl.style.display = 'flex';
    body.inert = true;
    body.setAttribute('aria-hidden', 'true');
    setPreservedMinimized(true);
    resumeBtn.focus();
  }

  function show(): boolean {
    if (openState) return true;
    if (!currentIframe) return false;
    const restoreFocus = doc.activeElement === resumeBtn;
    reveal();
    if (restoreFocus) closeBtn.focus();
    return true;
  }

  function close(): void {
    if (!openState && !currentIframe) return;
    const closingGeneration = ++lifecycleGeneration;
    openState = false;
    isVoiceMode = false;
    preserveOnDismiss = false;
    setPreservedMinimized(false);
    pnl.classList.remove('open', 'pw-pnl-minimized');
    body.inert = false;
    body.removeAttribute('aria-hidden');
    closeBtn.setAttribute('aria-label', 'Close panel');
    setTimeoutFn(() => {
      if (closingGeneration !== lifecycleGeneration) return;
      // A newer open() may have run during the slide-out animation (voice
      // "start perio" Strict Mode remount, live→edit force-reopen). Do not
      // tear down a panel that is open again.
      if (openState) return;
      if (currentIframe) {
        if (isPreloadedIframe(currentIframe)) {
          parkPreloaded(currentIframe);
        } else if (currentIframe.parentNode) {
          currentIframe.parentNode.removeChild(currentIframe);
        }
      }
      currentIframe = null;
      pnl.style.display = 'none';
      body.style.width = '';
      body.style.height = '';
      body.style.borderRadius = '';
    }, CLOSE_ANIMATION_MS);
    if (callbacks.onClose) callbacks.onClose();
  }

  function open(
    url: string,
    bottomRight?: boolean,
    options: InlinePanelOpenOptions = {},
  ): void {
    if (preservedMinimized && currentIframe) {
      const nextUrl = new URL(url, doc.baseURI).href;
      if (options.preserveOnDismiss === true && currentIframe.src === nextUrl) {
        show();
      }
      return;
    }

    const preloaded = getPreloaded ? getPreloaded(url) : null;

    if (currentIframe && currentIframe !== preloaded) {
      if (isPreloadedIframe(currentIframe)) {
        parkPreloaded(currentIframe);
      } else if (currentIframe.parentNode) {
        currentIframe.parentNode.removeChild(currentIframe);
      }
      currentIframe = null;
    }

    isVoiceMode = !!bottomRight;
    preserveOnDismiss = options.preserveOnDismiss === true;
    closeBtn.setAttribute(
      'aria-label',
      preserveOnDismiss ? 'Minimize perio chart' : 'Close panel',
    );
    pnl.classList.toggle('pw-pnl-voice', isVoiceMode);

    if (preloaded) {
      currentIframe = preloaded;
      currentIframe.style.display = 'block';
      showLoadingUntilLoaded(preloaded);

      reveal();
      if (preloaded.hasAttribute(PRELOAD_LOADED_ATTR)) {
        postPanelOpen(preloaded, url);
      } else {
        preloaded.addEventListener(
          'load',
          () => {
            if (openState && currentIframe === preloaded) {
              postPanelOpen(preloaded, url);
            }
          },
          { once: true },
        );
      }
      if (callbacks.onOpen) callbacks.onOpen(url);
      return;
    }

    if (currentIframe && currentIframe.parentNode) {
      currentIframe.parentNode.removeChild(currentIframe);
      currentIframe = null;
    }
    loading.style.display = 'flex';

    const iframe = doc.createElement('iframe');
    iframe.src = url;
    iframe.setAttribute(
      'sandbox',
      isVoiceMode ? VOICE_PANEL_SANDBOX : INLINE_PANEL_SANDBOX,
    );
    // SCRIBE-1301: `clipboard-write` is granted for EVERY panel iframe, not just
    // voice mode, so the perio "Copy link" action works in the full-width perio
    // breakout overlay (opened via `openPanel`, which is not voice mode) as well
    // as in the voice side panel. Microphone + wake-lock stay voice-only.
    iframe.setAttribute(
      'allow',
      isVoiceMode
        ? 'microphone; clipboard-write; screen-wake-lock'
        : 'clipboard-write',
    );
    iframe.addEventListener('load', () => {
      loading.style.display = 'none';
      if (openState && currentIframe === iframe) {
        postPanelOpen(iframe, url);
      }
    });
    body.appendChild(iframe);
    currentIframe = iframe;

    reveal();
    if (callbacks.onOpen) callbacks.onOpen(url);
  }

  function onBackdropClick(ev: Event): void {
    if (ev.target === pnl) {
      if (preserveOnDismiss) hide();
      else close();
    }
  }
  pnl.addEventListener('click', onBackdropClick);
  closeBtn.addEventListener('click', ev => {
    ev.stopPropagation();
    if (preserveOnDismiss) hide();
    else close();
  });
  resumeBtn.addEventListener('click', ev => {
    ev.stopPropagation();
    show();
  });

  function onKeyDown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape' && openState) {
      if (preserveOnDismiss) hide();
      else close();
    }
  }
  doc.addEventListener('keydown', onKeyDown);

  function destroy(): void {
    close();
    doc.removeEventListener('keydown', onKeyDown);
    if (win) win.removeEventListener('message', handleResizeMessage);
    if (pnl.parentNode) pnl.parentNode.removeChild(pnl);
  }

  return {
    open,
    close,
    show,
    isOpen: () => openState,
    destroy,
    getBody: () => body,
    getRoot: () => pnl,
  };
}
